
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import SharePointConnectionService from '../services/connection/SharePointConnectionService';
import LoggingService from '../services/LoggingService';
import PnPjsService from '../services/connection/PnPjsService';
import SupabaseConnectionService from '../services/SupabaseConnectionService';
import { SharePointConnectionSettings } from '../services/SupabaseConnectionService';

// Default certificate and client settings
const DEFAULT_CERT_THUMBPRINT = '62037A13900CA3C31DFA6321FC110416CF85202E';
const DEFAULT_CLIENT_ID = 'dc97a0f2-d73a-4184-8bfe-0c10ed7ff5df';
const DEFAULT_TENANT_ID = 'b24752cf-dca1-4cd9-8094-aeeb93913ea5';

interface SharePointContextType {
  isInSharePoint: boolean;
  siteUrl: string | null;
  setSiteUrl: (url: string) => void;
  webUrl: string | null;
  userEmail: string | null;
  initGraphService: (context: any) => void;
  spfxContext: any;
  pnpjsConfig: {
    certificateThumbprint?: string;
  } | null;
  setPnPjsConfig: (config: { 
    certificateThumbprint?: string;
  }) => void;
  autoConfigureWithCertificate: () => Promise<boolean>;
  connectionStatus: 'connected' | 'not_connected' | 'connecting' | 'error';
}

const SharePointContext = createContext<SharePointContextType>({
  isInSharePoint: false,
  siteUrl: null,
  setSiteUrl: () => {},
  webUrl: null,
  userEmail: null,
  initGraphService: () => {},
  spfxContext: null,
  pnpjsConfig: null,
  setPnPjsConfig: () => {},
  autoConfigureWithCertificate: async () => false,
  connectionStatus: 'not_connected'
});

export const useSharePoint = () => useContext(SharePointContext);

export const SharePointProvider = ({ children }: { children: ReactNode }) => {
  const [isInSharePoint, setIsInSharePoint] = useState(false);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [webUrl, setWebUrl] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [spfxContext, setSpfxContext] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'not_connected' | 'connecting' | 'error'>('not_connected');
  const [pnpjsConfig, setPnPjsConfig] = useState<{
    certificateThumbprint?: string;
  } | null>({
    // Initialize with default certificate
    certificateThumbprint: DEFAULT_CERT_THUMBPRINT
  });

  const initGraphService = (context: any) => {
    if (!context) {
      LoggingService.warn('No SPFx context provided to initGraphService', {}, 'SharePointContext');
      return;
    }

    try {
      setSpfxContext(context);
      
      // Extract site and user information from context
      const pageContext = context.pageContext;
      if (pageContext) {
        setIsInSharePoint(true);
        
        // Site information
        if (pageContext.site) {
          setSiteUrl(pageContext.site.absoluteUrl);
        }
        
        // Web information
        if (pageContext.web) {
          setWebUrl(pageContext.web.absoluteUrl);
        }
        
        // User information
        if (pageContext.user) {
          setUserEmail(pageContext.user.email || pageContext.user.loginName);
        }
        
        LoggingService.info('SharePoint context initialized from SPFx', {
          siteUrl: pageContext.site?.absoluteUrl,
          webUrl: pageContext.web?.absoluteUrl,
          userEmail: pageContext.user?.email || pageContext.user?.loginName
        }, 'SharePointContext');

        // Initialize PnPjs with SPFx context
        PnPjsService.initialize({
          spfxContext: context,
          defaultSiteUrl: pageContext.site?.absoluteUrl
        }).catch(error => {
          LoggingService.error('Failed to initialize PnPjs with SPFx context', error, 'SharePointContext');
        });
      }
    } catch (error) {
      LoggingService.error('Error initializing SharePoint context', error, 'SharePointContext');
    }
  };

  // Auto-configure PnPjs using certificate-based authentication
  const autoConfigureWithCertificate = async (): Promise<boolean> => {
    try {
      setConnectionStatus('connecting');
      
      // Get SharePoint settings from Supabase
      let spSettings;
      let clientId = DEFAULT_CLIENT_ID;
      let certificateThumbprint = DEFAULT_CERT_THUMBPRINT;
      
      try {
        spSettings = await SupabaseConnectionService.getSharePointSettings();
        if (spSettings?.client_id) {
          clientId = spSettings.client_id;
        }
        if (spSettings?.certificate) {
          certificateThumbprint = spSettings.certificate;
        }
      } catch (error) {
        LoggingService.warn('Could not load SharePoint settings, using defaults', {}, 'SharePointContext');
      }
      
      // Configure PnPjs with certificate-based authentication
      setPnPjsConfig({
        certificateThumbprint
      });
      
      // Initialize PnPjs with certificate-based authentication
      await PnPjsService.initialize({
        msalConfig: {
          clientId,
          useCertificate: true,
          certificateThumbprint
        },
        defaultSiteUrl: siteUrl || (spSettings?.site_url || 'https://contoso.sharepoint.com')
      });
      
      LoggingService.info('PnPjs configured successfully using certificate-based authentication', {
        certificateThumbprint,
        siteUrl: siteUrl || spSettings?.site_url
      }, 'SharePointContext');
      
      setConnectionStatus('connected');
      return true;
    } catch (error) {
      LoggingService.error('Error auto-configuring PnPjs with certificate-based authentication', error, 'SharePointContext');
      setConnectionStatus('error');
      return false;
    }
  };

  // Initialize PnPjs when pnpjsConfig changes
  useEffect(() => {
    if (pnpjsConfig?.certificateThumbprint) {
      // Get SharePoint settings from Supabase to access client_id
      let clientId = DEFAULT_CLIENT_ID;
      SupabaseConnectionService.getSharePointSettings().then(settings => {
        if (settings?.client_id) {
          clientId = settings.client_id;
        }
        
        // Initialize PnPjs with the certificate
        PnPjsService.initialize({
          msalConfig: {
            clientId,
            useCertificate: true,
            certificateThumbprint: pnpjsConfig.certificateThumbprint
          },
          defaultSiteUrl: siteUrl || undefined
        }).catch(error => {
          LoggingService.error('Failed to initialize PnPjs with certificate', error, 'SharePointContext');
        });
      }).catch(error => {
        LoggingService.error('Failed to get SharePoint settings', error, 'SharePointContext');
        
        // Still try to initialize with default client ID
        PnPjsService.initialize({
          msalConfig: {
            clientId: DEFAULT_CLIENT_ID,
            useCertificate: true,
            certificateThumbprint: pnpjsConfig.certificateThumbprint
          },
          defaultSiteUrl: siteUrl || undefined
        }).catch(error => {
          LoggingService.error('Failed to initialize PnPjs with default client ID', error, 'SharePointContext');
        });
      });
    }
  }, [pnpjsConfig, siteUrl]);

  // Load saved site URL and certificate from Supabase on component mount
  useEffect(() => {
    const loadSavedSettings = async () => {
      try {
        const settings = await SupabaseConnectionService.getSharePointSettings();
        if (settings) {
          if (settings.site_url) {
            setSiteUrl(settings.site_url);
          }
          
          if (settings.certificate) {
            setPnPjsConfig({
              certificateThumbprint: settings.certificate
            });
          }
        }
      } catch (error) {
        LoggingService.warn('Error loading saved SharePoint settings, using defaults', {}, 'SharePointContext');
        // Still set the default certificate
        setPnPjsConfig({
          certificateThumbprint: DEFAULT_CERT_THUMBPRINT
        });
      }
    };
    
    loadSavedSettings();
  }, []);

  // Initialize PnPjs when siteUrl changes
  useEffect(() => {
    if (siteUrl && pnpjsConfig?.certificateThumbprint) {
      // Get SharePoint settings from Supabase to access client_id
      let clientId = DEFAULT_CLIENT_ID;
      SupabaseConnectionService.getSharePointSettings().then(settings => {
        if (settings?.client_id) {
          clientId = settings.client_id;
        }
        
        // Initialize PnPjs with certificate and site URL
        PnPjsService.initialize({
          msalConfig: {
            clientId,
            useCertificate: true,
            certificateThumbprint: pnpjsConfig.certificateThumbprint
          },
          defaultSiteUrl: siteUrl
        }).catch(error => {
          LoggingService.error('Failed to initialize PnPjs with site URL and certificate', error, 'SharePointContext');
        });
      }).catch(error => {
        LoggingService.error('Failed to get SharePoint settings, using default client ID', {}, 'SharePointContext');
        
        // Still try to initialize with default client ID
        PnPjsService.initialize({
          msalConfig: {
            clientId: DEFAULT_CLIENT_ID,
            useCertificate: true,
            certificateThumbprint: pnpjsConfig.certificateThumbprint
          },
          defaultSiteUrl: siteUrl
        }).catch(error => {
          LoggingService.error('Failed to initialize PnPjs with site URL and certificate', error, 'SharePointContext');
        });
      });
    }
  }, [siteUrl, pnpjsConfig]);

  return (
    <SharePointContext.Provider value={{ 
      isInSharePoint, 
      siteUrl, 
      setSiteUrl,
      webUrl, 
      userEmail, 
      initGraphService,
      spfxContext,
      pnpjsConfig,
      setPnPjsConfig,
      autoConfigureWithCertificate,
      connectionStatus
    }}>
      {children}
    </SharePointContext.Provider>
  );
};
